/**
 * Local-only note storage for the Long Message viewer modal.
 *
 * Notes are kept in `localStorage` under a single key (`openclaw.chat.viewer-notes.v1`)
 * so they survive page reloads but never leave the browser. Scoping by sessionKey
 * keeps per-conversation notes isolated; scoping by messageId (a stable hash of the
 * markdown content) keeps a note attached to the same logical answer across re-renders
 * and re-orderings within a session.
 *
 * Keep this module side-effect free (no DOM, no Lit). Tests run it in node + browser.
 */

const STORAGE_KEY = "openclaw.chat.viewer-notes.v1";

export type ViewerNoteRecord = {
  /** Plain-text user note for this message. */
  note: string;
  /** Epoch ms when the note was last saved. */
  updatedAt: number;
};

export type ViewerNotesMap = Record<string, ViewerNoteRecord>;

export type ViewerNoteScope = {
  sessionKey: string;
  messageId: string;
};

function safeStorage(): Storage | null {
  try {
    if (typeof localStorage === "undefined") {
      return null;
    }
    return localStorage;
  } catch {
    return null;
  }
}

/** Read the full notes map. Returns {} on any parse/missing-storage failure. */
export function loadViewerNotes(storage: Storage = safeStorage() as Storage): ViewerNotesMap {
  if (!storage) {
    return {};
  }
  let raw: string | null = null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return {};
  }
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const out: ViewerNotesMap = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (!v || typeof v !== "object") {
        continue;
      }
      const r = v as Record<string, unknown>;
      if (typeof r.note !== "string" || typeof r.updatedAt !== "number") {
        continue;
      }
      out[k] = { note: r.note, updatedAt: r.updatedAt };
    }
    return out;
  } catch {
    return {};
  }
}

function writeViewerNotes(notes: ViewerNotesMap, storage: Storage = safeStorage() as Storage) {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(notes));
  } catch {
    // quota / disabled storage — silently drop; UX is best-effort
  }
}

export function viewerNoteKey(scope: ViewerNoteScope): string {
  return `${scope.sessionKey}::${scope.messageId}`;
}

/** Returns the saved note for a message, or "" if none. */
export function getViewerNote(scope: ViewerNoteScope, storage?: Storage): ViewerNoteRecord | null {
  const key = viewerNoteKey(scope);
  const map = loadViewerNotes(storage);
  return map[key] ?? null;
}

/** Persist a note. Trims whitespace. Empty string clears the record. */
export function saveViewerNote(
  scope: ViewerNoteScope,
  note: string,
  storage?: Storage,
): ViewerNoteRecord | null {
  const trimmed = note.trim();
  const map = loadViewerNotes(storage);
  const key = viewerNoteKey(scope);
  if (!trimmed) {
    if (key in map) {
      delete map[key];
      writeViewerNotes(map, storage);
    }
    return null;
  }
  const record: ViewerNoteRecord = { note: trimmed, updatedAt: Date.now() };
  map[key] = record;
  writeViewerNotes(map, storage);
  return record;
}

/** Clear one note. */
export function clearViewerNote(scope: ViewerNoteScope, storage?: Storage): void {
  const map = loadViewerNotes(storage);
  const key = viewerNoteKey(scope);
  if (key in map) {
    delete map[key];
    writeViewerNotes(map, storage);
  }
}

/** Test helper — wipes everything. Not exported via index. */
export function _resetViewerNotesForTests(storage: Storage = safeStorage() as Storage): void {
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Build a stable message id from the markdown content. FNV-1a 32-bit; collisions
 * are vanishingly unlikely within a session and only cause a shared note, never
 * data loss. Keeps the id deterministic across reloads (no random UUIDs in storage).
 */
export function messageIdFromMarkdown(markdown: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < markdown.length; i++) {
    hash ^= markdown.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  // widen with length so two very different documents that happen to collide on
  // FNV are still distinguishable.
  return `${hash.toString(16).padStart(8, "0")}-${markdown.length.toString(16)}`;
}
